import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import {
  FileText, Download, CheckCircle2, AlertCircle, Loader2, Lock, Calendar, DollarSign,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

const fmtMoney = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Form1099Page = () => {
  const [taxYear, setTaxYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showOnlyRequired, setShowOnlyRequired] = useState(true);

  const load = useCallback(async (yr) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/form-1099/summary?tax_year=${yr}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load 1099 summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(taxYear); }, [taxYear, load]);

  const downloadPdf = async (item) => {
    setBusyId(`pdf-${item.w9_form_id}`);
    try {
      const res = await axios.get(
        `${API}/form-1099/${item.w9_form_id}/pdf?tax_year=${taxYear}`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `1099-NEC-${taxYear}-${(item.legal_name || 'contractor').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('1099 PDF downloaded');
    } catch (e) {
      toast.error('PDF download failed');
    } finally {
      setBusyId(null);
    }
  };

  const finalize = async (item) => {
    setBusyId(`finalize-${item.w9_form_id}`);
    try {
      await axios.post(
        `${API}/form-1099/${item.w9_form_id}/finalize?tax_year=${taxYear}`,
        {},
        { withCredentials: true },
      );
      toast.success(`1099 finalized for ${item.legal_name}`);
      await load(taxYear);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Finalize failed');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    return showOnlyRequired ? data.items.filter((i) => i.meets_threshold) : data.items;
  }, [data, showOnlyRequired]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="form-1099-page">
        <header className="mb-6">
          <h1
            className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: 'Work Sans, sans-serif' }}
          >
            Form 1099-NEC
          </h1>
          <p
            className="text-sm text-slate-600 mt-1 max-w-2xl"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            Generate Copy B (recipient) statements for contractors who received $600+ in nonemployee compensation this tax year.
            Required by IRS §6041A.
          </p>
        </header>

        {/* Controls */}
        <div
          className="flex flex-wrap items-center gap-3 mb-5"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Calendar className="w-4 h-4 text-slate-500" />
            <span className="font-semibold">Tax year</span>
            <select
              data-testid="form-1099-year-select"
              value={taxYear}
              onChange={(e) => setTaxYear(parseInt(e.target.value, 10))}
              className="border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={showOnlyRequired}
              data-testid="form-1099-only-required"
              onChange={(e) => setShowOnlyRequired(e.target.checked)}
              className="rounded border-slate-300"
            />
            Show only contractors meeting $600 threshold
          </label>
        </div>

        {/* Summary KPIs */}
        {data && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <KpiCard
              testid="kpi-1099-total-contractors"
              icon={FileText}
              label="W-9s on file"
              value={data.total_contractors}
            />
            <KpiCard
              testid="kpi-1099-requires"
              icon={AlertCircle}
              label="Requires 1099-NEC"
              value={data.requires_1099}
              color={data.requires_1099 > 0 ? '#DC2626' : '#34C759'}
            />
            <KpiCard
              testid="kpi-1099-total-paid"
              icon={DollarSign}
              label="Total reportable paid"
              value={fmtMoney(data.total_reportable_paid)}
            />
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="form-1099-table">
          {loading ? (
            <div className="p-10 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading 1099 summary…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {showOnlyRequired
                ? 'No contractors crossed the $600 threshold for this tax year.'
                : 'No W-9 forms on file yet.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <thead className="bg-slate-50">
                  <tr>
                    <Th>Contractor</Th>
                    <Th>TIN</Th>
                    <Th right>YTD Gross</Th>
                    <Th center>Loads</Th>
                    <Th center>Threshold</Th>
                    <Th center>Status</Th>
                    <Th right>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => (
                    <tr
                      key={item.w9_form_id}
                      data-testid={`form-1099-row-${item.w9_form_id}`}
                      className="border-t border-slate-100 hover:bg-slate-50"
                    >
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-900">{item.legal_name || '—'}</div>
                        {item.business_name && <div className="text-xs text-slate-500">{item.business_name}</div>}
                        {item.contractor_email && (
                          <div className="text-[11px] text-slate-400">{item.contractor_email}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-slate-700">
                        {item.tin_kind?.toUpperCase()} • {item.tin_masked}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-semibold text-slate-900">
                        {fmtMoney(item.ytd_gross)}
                      </td>
                      <td className="py-3 px-4 text-center text-slate-600">{item.ytd_load_count}</td>
                      <td className="py-3 px-4 text-center">
                        {item.meets_threshold ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-red-100 text-red-700">
                            <AlertCircle className="w-3 h-3" /> Required
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-slate-100 text-slate-500">
                            Below
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {item.finalized ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700">
                            <Lock className="w-3 h-3" /> Finalized
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-slate-100 text-slate-600">
                            Draft
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            data-testid={`form-1099-pdf-${item.w9_form_id}`}
                            disabled={busyId === `pdf-${item.w9_form_id}`}
                            onClick={() => downloadPdf(item)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 hover:border-slate-400 text-slate-700 rounded text-xs font-semibold disabled:opacity-50"
                          >
                            {busyId === `pdf-${item.w9_form_id}` ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Download className="w-3 h-3" />
                            )}
                            PDF
                          </button>
                          {!item.finalized && (
                            <button
                              data-testid={`form-1099-finalize-${item.w9_form_id}`}
                              disabled={busyId === `finalize-${item.w9_form_id}`}
                              title={item.meets_threshold
                                ? 'Lock this 1099-NEC record for the tax year'
                                : `Below $600 IRS threshold — 1099-NEC not required for ${taxYear}`}
                              onClick={() => finalize(item)}
                              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-50 ${
                                item.meets_threshold
                                  ? 'bg-[#002FA7] hover:bg-[#00247D] text-white'
                                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-300'
                              }`}
                            >
                              {busyId === `finalize-${item.w9_form_id}` ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <CheckCircle2 className="w-3 h-3" />
                              )}
                              Finalize
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="mt-4 text-xs text-slate-500 max-w-3xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          Note: CivilX TMS generates the recipient Copy B for your records. The official red-ink Copy A must be submitted to the
          IRS electronically via the FIRE system or an approved e-filer. State filings vary — consult your tax advisor.
        </p>
      </div>
    </DashboardLayout>
  );
};

const KpiCard = ({ testid, icon: Icon, label, value, color = '#002FA7' }) => (
  <div
    data-testid={testid}
    className="bg-white border border-slate-200 rounded-md p-5"
  >
    <div className="flex items-center gap-3">
      <Icon className="w-7 h-7" style={{ color }} strokeWidth={2} />
      <div>
        <p
          className="text-xs font-semibold uppercase tracking-widest text-slate-500"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          {label}
        </p>
        <p
          className="text-2xl font-bold text-slate-900"
          style={{ fontFamily: 'Work Sans, sans-serif' }}
        >
          {value}
        </p>
      </div>
    </div>
  </div>
);

const Th = ({ children, right, center }) => (
  <th
    className={`py-3 px-4 text-[10px] font-bold uppercase tracking-widest text-slate-500 ${
      right ? 'text-right' : center ? 'text-center' : 'text-left'
    }`}
  >
    {children}
  </th>
);

export default Form1099Page;
